import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { services } from "@/data/services";
import { BLOG_POSTS } from "@/data/blog-posts";
import { locationCityList } from "@/data/location-cities";
import { Home, Briefcase, PenTool, MapPin, FileText, Phone, Info, Heart, Shield } from "lucide-react";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/sitemap")({
  component: SitemapPage,
  head: () => ({
    meta: [
      { title: "Sitemap — SimpleTech" },
      { name: "description", content: "Navigate SimpleTech's website with our complete sitemap. Find all pages, services, blog posts, and locations." },
      { property: "og:title", content: "Sitemap — SimpleTech" },
      { property: "og:description", content: "Complete sitemap for SimpleTech's website." },
      seoOgUrl("/sitemap"),
    ],
    links: [seoCanonicalLink("/sitemap")],
  }),
});

interface SectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, children }: SectionProps) {
  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-sky-100 grid place-items-center">
          <Icon className="w-5 h-5 text-sky-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      </div>
      <div className="ml-13 pl-2">
        {children}
      </div>
    </div>
  );
}

function SitemapLink({ to, children, params }: { to: string; children: React.ReactNode; params?: Record<string, string> }) {
  return (
    <li className="py-2 border-b border-slate-100 last:border-0">
      <Link
        to={to}
        params={params}
        className="text-slate-700 hover:text-sky-600 transition-colors flex items-center gap-2"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
        {children}
      </Link>
    </li>
  );
}

function SitemapAnchor({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li className="py-2 border-b border-slate-100 last:border-0">
      <a
        href={href}
        className="text-slate-700 hover:text-sky-600 transition-colors flex items-center gap-2"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
        {children}
      </a>
    </li>
  );
}

function SitemapPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sm font-bold tracking-widest text-violet-700 uppercase mb-4">
              Navigation
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight mb-4">
              Sitemap
            </h1>
            <p className="text-slate-600 leading-relaxed max-w-xl mb-14">
              A complete guide to every page on the SimpleTech website. Click any link to jump directly to that section.
            </p>

            <div className="grid md:grid-cols-2 gap-x-12">
              {/* Left column */}
              <div>
                <Section icon={Home} title="Main Pages">
                  <ul>
                    <SitemapLink to="/">Home</SitemapLink>
                    <SitemapLink to="/about">About Us</SitemapLink>
                    <SitemapLink to="/contact">Contact</SitemapLink>
                    <SitemapLink to="/giving-back">Giving Back</SitemapLink>
                    <SitemapLink to="/blog">Blog</SitemapLink>
                  </ul>
                </Section>

                <Section icon={Briefcase} title="Services">
                  <ul>
                    <SitemapLink to="/services">All Services</SitemapLink>
                    {services.map((s) => (
                      <SitemapLink key={s.slug} to="/services/$slug" params={{ slug: s.slug }}>
                        {s.title}
                      </SitemapLink>
                    ))}
                  </ul>
                </Section>

                <Section icon={MapPin} title="Service Locations">
                  <ul>
                    <SitemapLink to="/services/managed-it-services">Managed IT Services Overview</SitemapLink>
                    {locationCityList.map((city) => (
                      <SitemapLink
                        key={city.slug}
                        to="/services/managed-it-services/$city"
                        params={{ city: city.slug }}
                      >
                        Managed IT Services — {city.name}, {city.region}
                      </SitemapLink>
                    ))}
                    <SitemapLink to="/tech-support-montclair-ca">
                      Tech Support — Montclair, CA
                    </SitemapLink>
                    <SitemapLink to="/voip-montclair-ca">
                      VoIP Phone Systems — Montclair, CA
                    </SitemapLink>
                    <SitemapLink to="/web-design-montclair-ca">
                      Web Design — Montclair, CA
                    </SitemapLink>
                    <SitemapLink to="/software-integration-montclair-ca">
                      Software Integration — Montclair, CA
                    </SitemapLink>
                    <SitemapLink to="/senior-tech-support-montclair-ca">
                      Senior Tech Support — Montclair, CA
                    </SitemapLink>

                  </ul>
                </Section>
              </div>

              {/* Right column */}
              <div>
                <Section icon={PenTool} title="Blog Posts">
                  <ul>
                    {BLOG_POSTS.map((post) => (
                      <SitemapLink key={post.slug} to="/articles/$slug" params={{ slug: post.slug }}>
                        {post.title}
                      </SitemapLink>
                    ))}
                  </ul>
                </Section>

                <Section icon={FileText} title="Legal & Policies">
                  <ul>
                    <SitemapLink to="/privacy-policy">Privacy Policy</SitemapLink>
                    <SitemapLink to="/terms-and-conditions">Terms and Conditions</SitemapLink>
                    <SitemapLink to="/accessibility-statement">Accessibility Statement</SitemapLink>
                    <SitemapLink to="/sitemap">Sitemap</SitemapLink>
                  </ul>
                </Section>

                <Section icon={Phone} title="Contact Us">
                  <ul>
                    <SitemapAnchor href="tel:9092559112">Call (909) 255-9112</SitemapAnchor>
                    <SitemapAnchor href="mailto:contact@simpletechhelper.com">Email contact@simpletechhelper.com</SitemapAnchor>
                    <SitemapAnchor href="https://www.google.com/maps/dir/?api=1&destination=8880+N+Benson+Ave+%23121+Montclair+CA+91763">Get Directions to Our Office</SitemapAnchor>
                  </ul>
                </Section>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
